import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import * as _ from 'lodash';
import { ForecastingBudgetingService } from './forecasting.budgeting.service';

export enum YearColumnType {
    BudgetFmv,
    BudgetFmvVariance,
    Fmv,
    FmvChange,
    BudgetAltFmv,
    BudgetAltFmvVariance,
    Fmv_A,
    FmvChange_A,
    BudgetTax,
    BudgetTaxVariance,
    Tax,
    TaxChange,
    TaxableValue,
    BudgetTaxableValue,
}

export interface ForecastBudgetYearCellRendererParams extends ICellRendererParams {
    year: number;
    yearColumnType: number;
    siteRollup?: boolean;
    companyBudgetId?: number;
}

@Component({
    selector: 'forecast-budget-year-cell',
    styleUrls: ['./agGridForecastBudgetYearCellRender.component.scss'],
    template: `
        @if (assessment) {
            <div>
                @if (params.yearColumnType === YearColumnType.Fmv) {
                    <div>{{ assessment.totalFMV | number: '1.0-0' }}</div>
                }
                @if (params.yearColumnType === YearColumnType.FmvChange) {
                    <div>{{ assessment.yoyPctChg | percent: '1.3-3' }}</div>
                }
                @if (params.yearColumnType === YearColumnType.Fmv_A) {
                    <div>{{ assessment.altFMV | number: '1.0-0' }}</div>
                }
                @if (params.yearColumnType === YearColumnType.FmvChange_A) {
                    <div>{{ assessment.altYoyPctChg | percent: '1.3-3' }}</div>
                }
                @if (params.yearColumnType === YearColumnType.TaxableValue) {
                    <div style="display: flex; gap: 5px; justify-content: space-between; align-items: center;">
                        @if (assessment.taxableValue || assessment.taxableValue === 0) {
                            <div
                                [popover]="taxableDetails"
                                [popoverTitle]="params.year + ' Taxable Values'"
                                container="body"
                                boundariesElement="viewport"
                                [outsideClick]="true"
                                containerClass="taxable-details-popover"
                                (onShown)="getTaxableDetails(false)"
                            >
                                <i class="fa fa-info-circle" style="cursor: pointer;"></i>
                            </div>
                        }

                        <span>{{ assessment.taxableValue | number: '1.0-0' }}</span>
                    </div>
                }
            </div>
        }
        @if (taxYear) {
            <div>
                @if (params.yearColumnType === YearColumnType.Tax) {
                    <div>{{ taxYear.totalTax | currency: 'USD' : 'symbol-narrow' }}</div>
                }
                @if (params.yearColumnType === YearColumnType.TaxChange) {
                    <div>{{ taxYear.yoyPctChg | percent: '1.3-3' }}</div>
                }
            </div>
        }
        @if (budget) {
            <div>
                @if (params.yearColumnType === YearColumnType.BudgetFmv) {
                    <div>{{ budget.budgetFmv | number: '1.0-0' }}</div>
                }
                @if (
                    params.yearColumnType === YearColumnType.BudgetFmvVariance &&
                    !isInvalidNumber(budget.budgetFmvVariance)
                ) {
                    <div>{{ budget.budgetFmvVariance | number: '1.0-0' }}</div>
                }
                @if (params.yearColumnType === YearColumnType.BudgetAltFmv) {
                    <div>{{ budget.budgetAltFmv | number: '1.0-0' }}</div>
                }
                @if (
                    params.yearColumnType === YearColumnType.BudgetAltFmvVariance &&
                    !isInvalidNumber(budget.budgetAltFmvVariance)
                ) {
                    <div>{{ budget.budgetAltFmvVariance | number: '1.0-0' }}</div>
                }
                @if (params.yearColumnType === YearColumnType.BudgetTax) {
                    <div>{{ budget.budgetTax | currency: 'USD' : 'symbol-narrow' }}</div>
                }
                @if (
                    params.yearColumnType === YearColumnType.BudgetTaxVariance &&
                    !isInvalidNumber(budget.budgetTaxVariance)
                ) {
                    <div>{{ budget.budgetTaxVariance | currency: 'USD' : 'symbol-narrow' }}</div>
                }
                @if (params.yearColumnType === YearColumnType.BudgetTaxableValue) {
                    <div style="display: flex; gap: 5px; justify-content: space-between; align-items: center;">
                        @if (budget.budgetTaxableValue || budget.budgetTaxableValue === 0) {
                            <div
                                [popover]="taxableDetails"
                                [popoverTitle]="params.year + ' Bdg Taxable Values'"
                                container="body"
                                boundariesElement="viewport"
                                [outsideClick]="true"
                                containerClass="taxable-details-popover"
                                (onShown)="getTaxableDetails(true)"
                            >
                                <i class="fa fa-info-circle" style="cursor: pointer;"></i>
                            </div>
                        }

                        <span>{{ budget.budgetTaxableValue | number: '1.0-0' }}</span>
                    </div>
                }
            </div>
        }

        <ng-template #taxableDetails>
            @if (taxableValuesLoading) {
                <div class="text-center p-2">
                    <i class="fa fa-spinner fa-spin fa-3x"></i>
                </div>
            } @else {
                @if (!taxableValuesLoading && taxableValues?.length) {
                    <table class="table table-sm table-striped taxable-details-table">
                        <thead>
                            <tr>
                                <th scope="col" style="text-align: left;">Taxable</th>
                                <th scope="col">Assessed Value</th>
                                <th scope="col">Market Value</th>
                                <th scope="col">Alt Assessed Value</th>
                                <th scope="col">Alt Market Value</th>
                                <th scope="col">Exemptions Assessed Value</th>
                                <th scope="col">Exemptions Market Value</th>
                                <th scope="col">Taxable Assessed Value</th>
                                <th scope="col">Taxable Market Value</th>
                                <th scope="col">Rate</th>
                                <th scope="col">Tax Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (row of taxableValues; track row) {
                                <tr>
                                    <td style="text-align: left;"
                                        >Taxable{{ row.taxableSequence === 1 ? '' : row.taxableSequence }}</td
                                    >
                                    <td>{{ row.assessedValue | number: '1.0-0' }}</td>
                                    <td>{{ row.marketValue | number: '1.0-0' }}</td>
                                    <td>{{
                                        row.altAssessedValue ? (row.altAssessedValue | number: '1.0-0') : null
                                    }}</td>
                                    <td>{{ row.altMarketValue ? (row.altMarketValue | number: '1.0-0') : null }}</td>
                                    <td>
                                        @if (row.exemptionsAssessedValue < 0) {
                                            <span>{{ -row.exemptionsAssessedValue | number: '1.0-0' }}</span>
                                        }
                                    </td>
                                    <td>
                                        @if (row.exemptionsMarketValue < 0) {
                                            <span>{{ -row.exemptionsMarketValue | number: '1.0-0' }}</span>
                                        }
                                    </td>
                                    <td>{{ row.taxableAssessedValue | number: '1.0-0' }}</td>
                                    <td>{{ row.taxableMarketValue | number: '1.0-0' }}</td>
                                    <td>{{ row.taxRate | percent: '1.6-6' }}</td>
                                    <td>{{ row.taxAmount | currency: 'USD' : 'symbol-narrow' }}</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
                <p class="m-2" style="max-width: 875px;">
                    <em>{{ footnote }}</em>
                </p>
            }
        </ng-template>
    `,
    standalone: false,
})
export class ForecastBudgetYearCellRendererComponent implements ICellRendererAngularComp {
    constructor(private forecastingBudgetingService: ForecastingBudgetingService) {}

    params: ForecastBudgetYearCellRendererParams;
    YearColumnType = YearColumnType;

    assessment: Core.ForecastGridEntryAssessment;
    taxYear: Core.ForecastGridEntryTax;
    budget: Core.ForecastGridEntryBudget;

    taxableValues: Core.ForecastBudgetTaxableValueDetail[];
    taxableValuesLoading = false;
    footnote = '';

    agInit(params: ForecastBudgetYearCellRendererParams): void {
        this.params = params;

        this._getYear();
    }

    isInvalidNumber(value: string | number): boolean {
        return value === null || value === undefined || value === '' || value === '.';
    }

    refresh(): boolean {
        return false; // tells grid to handle refreshing
    }

    async getTaxableDetails(isForBudget: boolean): Promise<void> {
        const request: Core.ForecastBudgetTaxableValuesRequest = {
            annualYear: this.params.year,
            entityId: this.params.data.entityId,
            entityTypeId: this.params.data.entityTypeId,
            tlCompanyID: this.forecastingBudgetingService.tlCompany.companyID,
        };

        this.taxableValuesLoading = true;
        try {
            const { taxableValues } = isForBudget
                ? await this.forecastingBudgetingService.getBudgetTaxableValuesDetail(
                      this.params.companyBudgetId,
                      request,
                  )
                : await this.forecastingBudgetingService.getForecastTaxableValuesDetail(request);

            const numParcels = _.chain(taxableValues).map('parcelId').uniq().value().length;

            if (this.params.siteRollup && numParcels > 1) {
                const taxableValuesForSeq1 = taxableValues.filter((x) => x.taxableSequence === 1);

                const assessedValue = _.sumBy(taxableValuesForSeq1, 'assessedValue');
                const marketValue = _.sumBy(taxableValuesForSeq1, 'marketValue');
                const altAssessedValue = _.sumBy(taxableValuesForSeq1, 'altAssessedValue');
                const altMarketValue = _.sumBy(taxableValuesForSeq1, 'altMarketValue');
                const exemptionsAssessedValue = _.sumBy(taxableValuesForSeq1, 'exemptionsAssessedValue');
                const exemptionsMarketValue = _.sumBy(taxableValuesForSeq1, 'exemptionsMarketValue');
                const taxableMarketValue = _.sumBy(taxableValuesForSeq1, 'taxableMarketValue');
                const taxableAssessedValue = _.sumBy(taxableValuesForSeq1, 'taxableAssessedValue');
                const taxAmount = _.sumBy(taxableValuesForSeq1, 'taxAmount');
                const taxRate = new Decimal(taxAmount).dividedBy(taxableAssessedValue || 1).toNumber();

                this.taxableValues = [
                    {
                        taxableSequence: 1,
                        parcelId: null,
                        assessedValue,
                        marketValue,
                        altAssessedValue,
                        altMarketValue,
                        exemptionsAssessedValue,
                        exemptionsMarketValue,
                        taxableMarketValue,
                        taxableAssessedValue,
                        taxRate,
                        taxAmount,
                        parcelAcctNum: '',
                        annualYear: this.params.year,
                    },
                ];

                this.footnote = `Values represent the sum for ${numParcels} parcels of the site.`;

                if (_.some(taxableValues, (x) => x.taxableSequence > 1)) {
                    this.footnote +=
                        ' One or more parcels have multiple Taxable Market Values; view the individual parcels of the site for further detail.';
                }
            } else {
                this.taxableValues = taxableValues;
            }
        } finally {
            this.taxableValuesLoading = false;
        }
    }

    private _getYear() {
        switch (this.params.yearColumnType) {
            case YearColumnType.Fmv:
            case YearColumnType.FmvChange:
            case YearColumnType.Fmv_A:
            case YearColumnType.FmvChange_A:
            case YearColumnType.TaxableValue:
                this.assessment = _.find(this.params.data.assessments as Core.ForecastGridEntryAssessment[], {
                    annualYear: this.params.year,
                });
                break;
            case YearColumnType.Tax:
            case YearColumnType.TaxChange:
                this.taxYear = _.find(this.params.data.taxes as Core.ForecastGridEntryTax[], {
                    annualYear: this.params.year,
                });
                break;
            case YearColumnType.BudgetFmv:
            case YearColumnType.BudgetFmvVariance:
            case YearColumnType.BudgetAltFmv:
            case YearColumnType.BudgetAltFmvVariance:
            case YearColumnType.BudgetTax:
            case YearColumnType.BudgetTaxVariance:
            case YearColumnType.BudgetTaxableValue:
                this.budget = _.find(this.params.data.budgetData as Core.ForecastGridEntryBudget[], {
                    annualYear: this.params.year,
                });
                break;
        }
    }
}
